import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  CheckCircle, ArrowRight, Sparkle, X, Sun, Moon, List, CaretLeft,
  Star, Buildings, Lightning, Shield, Folder, CurrencyDollar,
} from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { demoRequestsApi, billingApi } from '../lib/api';

// --- Pricing tiers (Ansarada / SAP hybrid) ---
const TIERS = [
  {
    key: 'free',
    name: 'Always Open Free',
    tagline: 'Kick the tires — zero commitment.',
    monthly: 0,
    annual: 0,
    badge: null,
    accent: '#6B7280',
    icon: Folder,
    features: [
      '1 Data Room (1 GB storage)',
      'Up to 3 users',
      'Basic CRM: 50 contacts',
      'Email support (48h)',
      'Community access',
    ],
    cta: { label: 'Get started free', action: 'register' },
  },
  {
    key: 'starter',
    name: 'Starter',
    tagline: 'For solo founders and small teams.',
    monthly: 49,
    annual: 39,   // per month, billed annually (~20% save)
    badge: null,
    accent: '#41B6E6',
    icon: Sparkle,
    features: [
      '3 Data Rooms (20 GB each)',
      'Up to 10 users',
      'Unlimited contacts & companies',
      'Deals pipeline + Tasks',
      'AI Deal Readiness (5 runs/mo)',
      'Priority email support (24h)',
    ],
    cta: { label: 'Start 30-day trial', action: 'checkout' },
  },
  {
    key: 'professional',
    name: 'Professional',
    tagline: 'Scale deals, automations & data rooms.',
    monthly: 99,
    annual: 79,
    badge: 'Most popular',
    accent: '#002677',
    icon: Star,
    features: [
      'Unlimited Data Rooms (100 GB each)',
      'Up to 50 users',
      'Workflow automations',
      'Full AI Copilot (unlimited runs)',
      'Finance, HR & Teams modules',
      'Webhooks & integrations',
      'Phone + chat support (8h)',
    ],
    cta: { label: 'Start 30-day trial', action: 'checkout' },
  },
  {
    key: 'business',
    name: 'Business',
    tagline: 'Multi-entity, per-seat elasticity.',
    monthlyPerUser: 149,
    annualPerUser: 119,
    perUser: true,
    badge: null,
    accent: '#8347AD',
    icon: Buildings,
    features: [
      'Everything in Professional',
      'Per-seat billing',
      'Advanced RBAC & SSO',
      'Multi-entity consolidation',
      'Custom fields & data model',
      'Audit trail export (SOC 2)',
      'Dedicated CSM',
    ],
    cta: { label: 'Start 30-day trial', action: 'checkout' },
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    tagline: 'Regulated industries, custom deployments.',
    monthly: null,
    annual: null,
    badge: null,
    accent: '#E4002B',
    icon: Shield,
    features: [
      'Everything in Business',
      'Unlimited users & storage',
      'Private cloud / on-prem',
      'SSO / SAML / SCIM',
      'Dedicated TAM & security review',
      'SLA 99.99% & 24/7 support',
      'White-labeling included',
    ],
    cta: { label: 'Contact sales', action: 'demo' },
  },
];

const COMPARE_ROWS = [
  { label: 'Data Rooms',              values: ['1 room · 1 GB', '3 rooms · 20 GB', 'Unlimited · 100 GB', 'Unlimited · 100 GB', 'Unlimited · Unlimited'] },
  { label: 'Users',                   values: ['3', '10', '50', 'Per-seat', 'Unlimited'] },
  { label: 'CRM (contacts/companies)',values: ['50 contacts', 'Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'] },
  { label: 'Deals + Pipeline',        values: [false, true, true, true, true] },
  { label: 'AI Deal Readiness',       values: [false, '5 runs/mo', 'Unlimited', 'Unlimited', 'Unlimited'] },
  { label: 'Workflow automation',     values: [false, false, true, true, true] },
  { label: 'Finance / HR / Teams',    values: [false, false, true, true, true] },
  { label: 'Webhooks & integrations', values: [false, false, true, true, true] },
  { label: 'Custom fields + RBAC',    values: [false, false, false, true, true] },
  { label: 'SSO / SAML',              values: [false, false, false, true, true] },
  { label: 'Audit trail export',      values: [false, false, false, true, true] },
  { label: 'White-label',             values: [false, false, false, false, true] },
  { label: 'SLA',                     values: ['None', '99.5%', '99.9%', '99.95%', '99.99%'] },
  { label: 'Support',                 values: ['Community', 'Email · 24h', 'Phone+Chat · 8h', 'Dedicated CSM', '24/7 TAM'] },
];

const FAQS = [
  { q: 'Can I switch plans anytime?', a: 'Yes. Upgrade, downgrade or cancel at any time from your Settings page. Prorated billing applies on upgrades.' },
  { q: 'Is the Free plan really free forever?', a: 'Yes — no credit card required. It\'s capped at 1 data room, 1 GB and 3 users, but there\'s no trial clock.' },
  { q: 'Do you offer a discount for annual billing?', a: 'Absolutely — annual billing saves you roughly 20% across every paid tier. The savings are reflected in the price shown when you toggle Annual.' },
  { q: 'Can I white-label BIL for my clients?', a: 'White-labeling (logo, palette, custom domain) is included with Enterprise and available as an add-on on Business.' },
  { q: 'How does the 30-day trial work?', a: 'You get full Professional-tier access for 30 days with an instant-activation Stripe Checkout. Your card is only charged for the first billing period — cancel any time within 30 days for a full refund.' },
  { q: 'Do you sign BAA / DPA?', a: 'Yes — on Business and Enterprise plans we sign both Data Processing Agreements (GDPR) and Business Associate Agreements (HIPAA where applicable).' },
];

export default function PricingPage() {
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [showDemo, setShowDemo] = useState(false);
  const [demoPlan, setDemoPlan] = useState('professional');
  const [openFaq, setOpenFaq] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // plan key being processed
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleCta = async (tier) => {
    if (tier.cta.action === 'register') {
      navigate('/register');
      return;
    }
    if (tier.cta.action === 'demo') {
      setDemoPlan(tier.key);
      setShowDemo(true);
      return;
    }
    if (tier.cta.action === 'checkout') {
      const packageId = `${tier.key}_${billing}`;
      setCheckoutLoading(tier.key);
      try {
        const { data } = await billingApi.checkoutSession(packageId, window.location.origin);
        if (data?.url) {
          window.location.href = data.url;
        } else {
          toast.error('Unable to start checkout. Please try again.');
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Unable to start checkout.');
      } finally {
        setCheckoutLoading(null);
      }
    }
  };

  const formatPrice = (tier) => {
    if (tier.monthly === null && tier.annual === null && !tier.perUser) return 'Custom';
    if (tier.perUser) {
      const v = billing === 'annual' ? tier.annualPerUser : tier.monthlyPerUser;
      return `$${v}`;
    }
    const v = billing === 'annual' ? tier.annual : tier.monthly;
    return v === 0 ? '$0' : `$${v}`;
  };

  const priceSuffix = (tier) => {
    if (tier.monthly === null && tier.annual === null && !tier.perUser) return '';
    if (tier.perUser) return '/user/mo';
    return tier.monthly === 0 ? ' forever' : '/mo';
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pricing-page">
      {/* === Shell Bar (reused SAP-style) === */}
      <header className="w-full bg-[#f5f6fa] dark:bg-muted/30 border-b border-border">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/login" className="flex items-center gap-3" data-testid="brand-block">
            <div
              className="h-14 w-16 flex items-center justify-center text-white font-heading font-bold text-xl tracking-tight"
              style={{ background: '#002677' }}
            >
              BIL
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors hidden sm:flex items-center gap-1"
              data-testid="back-to-home"
            >
              <CaretLeft size={14} weight="bold" /> Back
            </Link>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-sm hover:bg-accent/30 transition-colors"
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            <Link
              to="/login"
              className="p-2 rounded-sm hover:bg-accent/30 transition-colors"
              aria-label="Menu"
            >
              <List size={22} />
            </Link>
          </div>
        </div>
      </header>

      {/* === Hero === */}
      <section
        className="py-16 lg:py-24 border-b border-border"
        style={{
          background: 'linear-gradient(135deg, #eef2ff 0%, #e6efff 40%, #dae8fb 100%)',
        }}
      >
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
            Pricing
          </p>
          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0a0a0a] dark:text-foreground max-w-4xl mx-auto">
            Plans that grow with every deal, room and region.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free. Scale as you close more deals. Only pay for seats and
            rooms you actually use — no hidden overage fees.
          </p>

          {/* Monthly / Annual toggle */}
          <div className="mt-10 inline-flex items-center bg-white rounded-full p-1 shadow-sm border border-border" data-testid="billing-toggle">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                billing === 'monthly'
                  ? 'bg-[#002677] text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                billing === 'annual'
                  ? 'bg-[#002677] text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="billing-annual"
            >
              Annual
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full tracking-wider uppercase font-semibold ${
                billing === 'annual' ? 'bg-[#00B140] text-white' : 'bg-[#00B140]/15 text-[#00813A]'
              }`}>
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* === Tier cards === */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="pricing-tiers">
            {TIERS.map((tier) => {
              const Icon = tier.icon;
              const isPopular = tier.badge === 'Most popular';
              return (
                <div
                  key={tier.key}
                  className={`relative bg-card border rounded-sm p-6 flex flex-col transition-all ${
                    isPopular
                      ? 'border-[#002677] shadow-2xl lg:-translate-y-3 lg:scale-[1.02]'
                      : 'border-border hover:border-foreground/30 hover:shadow-lg'
                  }`}
                  data-testid={`tier-${tier.key}`}
                >
                  {tier.badge && (
                    <span
                      className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold text-white rounded-full"
                      style={{ background: tier.accent }}
                    >
                      {tier.badge}
                    </span>
                  )}
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="h-10 w-10 rounded-sm flex items-center justify-center"
                      style={{ background: `${tier.accent}1A` }}
                    >
                      <Icon size={20} weight="duotone" color={tier.accent} />
                    </div>
                  </div>
                  <h3 className="font-heading text-xl font-semibold tracking-tight">
                    {tier.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 mb-5 min-h-[32px]">
                    {tier.tagline}
                  </p>

                  {/* Price */}
                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-4xl font-bold tracking-tight">
                        {formatPrice(tier)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {priceSuffix(tier)}
                      </span>
                    </div>
                    {billing === 'annual' && tier.monthly > 0 && !tier.perUser && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        billed annually · <span className="line-through">${tier.monthly}</span>/mo
                      </p>
                    )}
                    {billing === 'annual' && tier.perUser && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        billed annually · <span className="line-through">${tier.monthlyPerUser}</span>/user/mo
                      </p>
                    )}
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={() => handleCta(tier)}
                    disabled={checkoutLoading === tier.key}
                    className={`w-full rounded-sm font-medium h-11 mb-6 ${isPopular ? '' : 'variant-outline'}`}
                    style={isPopular
                      ? { background: tier.accent }
                      : { background: 'transparent', color: tier.accent, border: `2px solid ${tier.accent}` }
                    }
                    data-testid={`cta-${tier.key}`}
                  >
                    {checkoutLoading === tier.key ? 'Redirecting…' : tier.cta.label}
                    {checkoutLoading !== tier.key && <ArrowRight size={14} className="ml-2" weight="bold" />}
                  </Button>

                  {/* Features */}
                  <ul className="space-y-2.5 flex-1" data-testid={`features-${tier.key}`}>
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle
                          size={16}
                          weight="fill"
                          className="flex-shrink-0 mt-0.5"
                          style={{ color: tier.accent }}
                        />
                        <span className="text-foreground/90 leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Hero CTA row */}
          <div className="mt-14 bg-[#002677] rounded-sm p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="hero-cta">
            <div className="text-white max-w-xl">
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                Not sure which plan is right?
              </h3>
              <p className="mt-2 text-white/80 text-sm sm:text-base">
                Get a personalized 30-minute walkthrough with our solution architects — no sales pitch, real ROI math.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => { setDemoPlan('professional'); setShowDemo(true); }}
                size="lg"
                className="rounded-sm font-medium h-12 px-7 bg-white text-[#002677] hover:bg-white/90"
                data-testid="cta-request-demo"
              >
                Request a demo
                <ArrowRight size={16} className="ml-2" weight="bold" />
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-sm font-medium h-12 px-7 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#002677]"
              >
                <Link to="/register" data-testid="cta-start-free">Start free</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* === Comparison table === */}
      <section className="py-16 lg:py-20 bg-[#f5f6fa] dark:bg-muted/20 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              Compare plans
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Everything, side by side.
            </h2>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left font-semibold px-5 py-4 w-[26%]">Feature</th>
                  {TIERS.map((t) => (
                    <th key={t.key} className="text-left font-semibold px-4 py-4">
                      <span className="block text-foreground">{t.name}</span>
                      <span className="block text-[11px] text-muted-foreground font-normal mt-0.5">{formatPrice(t)}{priceSuffix(t)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, ri) => (
                  <tr key={ri} className="border-b border-border/50 last:border-b-0 hover:bg-accent/20">
                    <td className="px-5 py-3.5 text-muted-foreground">{row.label}</td>
                    {row.values.map((val, vi) => (
                      <td key={vi} className="px-4 py-3.5">
                        {val === true && <CheckCircle size={16} weight="fill" className="text-[#00B140]" />}
                        {val === false && <X size={14} weight="bold" className="text-muted-foreground/50" />}
                        {typeof val === 'string' && <span className="text-foreground/90">{val}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* === FAQ === */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">
              Questions
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Frequently asked.
            </h2>
          </div>
          <div className="divide-y divide-border border border-border rounded-sm bg-card" data-testid="faq-list">
            {FAQS.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} data-testid={`faq-${i}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-5 text-left hover:bg-accent/20 transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span className="font-medium text-foreground pr-4">{faq.q}</span>
                    <span
                      className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center transition-transform ${isOpen ? 'rotate-45' : ''}`}
                      style={{ background: '#002677' }}
                    >
                      <X size={12} weight="bold" color="white" className={isOpen ? '' : 'rotate-45'} />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === Footer CTA === */}
      <section className="py-12 bg-[#002677] text-white">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <p className="text-white/80">Ready to streamline every deal, document and supplier?</p>
          <div className="flex gap-3">
            <Link to="/register" className="underline hover:no-underline font-medium">Start free</Link>
            <span className="text-white/40">·</span>
            <button onClick={() => setShowDemo(true)} className="underline hover:no-underline font-medium">
              Request a demo
            </button>
          </div>
        </div>
      </section>

      {showDemo && (
        <DemoRequestModal
          defaultPlan={demoPlan}
          onClose={() => setShowDemo(false)}
        />
      )}
    </div>
  );
}

// =======================================================================
// === Demo Request Modal ===
// =======================================================================
function DemoRequestModal({ defaultPlan, onClose }) {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    company: '',
    phone: '',
    plan: defaultPlan || 'professional',
    company_size: '11-50',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await demoRequestsApi.create(form);
      setSubmitted(true);
      toast.success("Thanks! We'll reach out within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
      data-testid="demo-modal"
    >
      <div
        className="w-full max-w-lg bg-card border border-border rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-11 flex items-center justify-center text-white font-heading font-bold text-xs"
              style={{ background: '#002677' }}
            >
              BIL
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold tracking-tight">
                {submitted ? 'Request received' : 'Request a demo'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {submitted ? 'Our team will reach out within 1 business day.' : 'Tell us a little about your needs.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-sm hover:bg-accent/40"
            data-testid="demo-modal-close"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {submitted ? (
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-sm bg-[#00B140]/10 border border-[#00B140]/30">
              <CheckCircle size={22} weight="fill" className="text-[#00B140] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground">You&rsquo;re on the list, {form.full_name.split(' ')[0] || 'there'}.</p>
                <p className="text-muted-foreground mt-1">
                  A BIL solution architect will email {form.email} to schedule a 30-minute walkthrough tailored to your {form.company || 'team'}.
                </p>
              </div>
            </div>
            <Button
              onClick={onClose}
              className="w-full rounded-sm h-11"
              style={{ background: '#002677' }}
              data-testid="demo-modal-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="demo-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Full name *</Label>
                <Input
                  required
                  value={form.full_name}
                  onChange={(e) => update('full_name', e.target.value)}
                  className="mt-1.5 rounded-sm"
                  data-testid="demo-name"
                  placeholder="Jane Doe"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Work email *</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="mt-1.5 rounded-sm"
                  data-testid="demo-email"
                  placeholder="jane@company.com"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Company</Label>
                <Input
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                  className="mt-1.5 rounded-sm"
                  data-testid="demo-company"
                  placeholder="ACME Corp"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Phone</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className="mt-1.5 rounded-sm"
                  data-testid="demo-phone"
                  placeholder="+1 555 123 4567"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Plan interest</Label>
                <Select value={form.plan} onValueChange={(v) => update('plan', v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="demo-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIERS.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Company size</Label>
                <Select value={form.company_size} onValueChange={(v) => update('company_size', v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="demo-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1–10</SelectItem>
                    <SelectItem value="11-50">11–50</SelectItem>
                    <SelectItem value="51-200">51–200</SelectItem>
                    <SelectItem value="201-1000">201–1,000</SelectItem>
                    <SelectItem value="1000+">1,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">What are you hoping to solve?</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  className="mt-1.5 rounded-sm min-h-[84px]"
                  data-testid="demo-message"
                  placeholder="e.g. We run 20 M&A deals/year and need data rooms + AI readiness scoring."
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-sm h-11 font-medium"
              style={{ background: '#002677' }}
              data-testid="demo-submit"
            >
              {submitting ? 'Submitting…' : 'Submit request'}
              <ArrowRight size={16} className="ml-2" weight="bold" />
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              By submitting you agree to our terms. We&rsquo;ll never share your info.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
